import { isNil } from 'lodash';
import { useEffect, useState } from 'react';

import StorefrontSearchApi from 'api/StorefrontSearch';

const buildEmptySearchResultState = () => ({
  searchItems: [],
  searchTotalItems: 0,
  searchHasError: false,
  searchItemsDisplayed: [],
  searchTotalItemsDisplayed: 0,
  paginationNextCursor: null,
  paginationHasMore: false,
});

const updateSearchResultState = ({
  prevSearchResultState,
  searchResultApis,
  shouldResetDisplayedItems,
}) => {
  const searchItemsDisplayed = shouldResetDisplayedItems
    ? searchResultApis?.searchItems
    : [...prevSearchResultState?.searchItemsDisplayed, ...searchResultApis?.searchItems];

  const totalItemsDisplayed = shouldResetDisplayedItems
    ? searchResultApis?.searchItems?.length
    : prevSearchResultState.searchItemsDisplayed?.length + searchResultApis?.searchItems?.length;

  const paginationHasMore = totalItemsDisplayed < searchResultApis?.searchTotalItems;

  return {
    searchItems: searchResultApis?.searchItems,
    searchTotalItems: searchResultApis?.searchTotalItems,
    searchHasError: false,
    searchItemsDisplayed,
    searchTotalItemsDisplayed: totalItemsDisplayed,
    paginationNextCursor: searchResultApis?.paginationNextCursor,
    paginationHasMore,
  };
};

const useShopifySearch = ({ paginationPageSize }) => {
  const [searchValueState, setSearchValueState] = useState(undefined);
  const [searchResultState, setSearchResultState] = useState(buildEmptySearchResultState());
  const [searchFetchingNextPage, setSearchFetchingNextPage] = useState(false);

  const shopifySearchCall = async ({ queryToSearch, paginationCurrentCursor }) => {
    if (isNil(queryToSearch)) {
      return;
    }

    try {
      const searchApiResult = await StorefrontSearchApi.search({
        queryToSearch,
        paginationPageSize,
        paginationNextCursor: paginationCurrentCursor,
      });

      setSearchResultState(prevState =>
        updateSearchResultState({
          prevSearchResultState: prevState,
          searchResultApis: searchApiResult,
          // if cursor is null means new query then reset displayed items
          shouldResetDisplayedItems: !paginationCurrentCursor ? true : false,
        })
      );

      setSearchFetchingNextPage(false);
    } catch (e) {
      setSearchResultState(buildEmptySearchResultState());
      setSearchFetchingNextPage(false);
    }
  };

  const searchFetchNextPage = () => {
    setSearchFetchingNextPage(true);
  };

  useEffect(() => {
    if (searchFetchingNextPage) {
      (async () => {
        await shopifySearchCall({
          queryToSearch: searchValueState,
          paginationCurrentCursor: searchResultState.paginationNextCursor,
        });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFetchingNextPage]);

  useEffect(() => {
    (async () => {
      await shopifySearchCall({
        queryToSearch: searchValueState,
        paginationCurrentCursor: null,
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValueState]);

  return {
    searchValueState,
    setSearchValueState,
    searchResultState,
    searchFetchNextPage,
  };
};

export { useShopifySearch };
